import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, X } from "lucide-react";

const NEW = "__new_supplier__";
const CATEGORIES = ["doors", "finishing", "cabinet_boxes", "moulding", "hardware", "paint", "general"];

// Supplier dropdown with an inline "+ Add New Supplier" creator (uses existing POST /suppliers; manager role enforced server-side).
export const SupplierPicker = ({ suppliers, value, onChange, onCreated, disabled, testId = "supplier-picker", defaultCategory = "doors" }) => {
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", category: defaultCategory });

  const save = async () => {
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim()) return toast.error("Company, contact and email are required");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/suppliers`, { ...form, phone: form.phone || null });
      toast.success(`${r.data.company_name} added`);
      onCreated?.(r.data);
      onChange(r.data.supplier_id);
      setAdding(false);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", category: defaultCategory });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add supplier"); }
    finally { setSaving(false); }
  };

  if (adding) return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 space-y-1.5" data-testid={`${testId}-new-form`}>
      <div className="flex items-center justify-between"><p className="text-xs font-semibold text-emerald-800">New supplier</p>
        <button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-600" aria-label="Cancel" data-testid={`${testId}-new-cancel`}><X className="w-3.5 h-3.5" /></button></div>
      <Input className="h-8 bg-white" placeholder="Company *" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid={`${testId}-new-company`} />
      <div className="grid grid-cols-2 gap-1.5">
        <Input className="h-8 bg-white" placeholder="Contact name *" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} data-testid={`${testId}-new-contact`} />
        <Input className="h-8 bg-white" placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid={`${testId}-new-phone`} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Input className="h-8 bg-white" type="email" placeholder="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} onKeyDown={e => e.key === "Enter" && save()} data-testid={`${testId}-new-email`} />
        <select className="h-8 border rounded-md px-2 text-sm bg-white" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid={`${testId}-new-category`}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <Button size="sm" className="h-8 w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={save} data-testid={`${testId}-new-save`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Save & select
      </Button>
    </div>
  );

  return (
    <select className="w-full border rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50" value={value || ""} disabled={disabled}
      onChange={e => { if (e.target.value === NEW) setAdding(true); else onChange(e.target.value); }} data-testid={testId}>
      <option value="">Select supplier…</option>
      {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.company_name}{s.category ? ` · ${s.category.replace(/_/g, " ")}` : ""}</option>)}
      <option value={NEW}>＋ Add New Supplier…</option>
    </select>
  );
};
